import React from 'react';
import { Box, CircularProgress, SxProps, Theme } from '@mui/material';
import { T } from '../../theme/tokens';

type SynVariant = 'primary' | 'accent' | 'ghost' | 'outline' | 'danger';
type SynSize    = 'sm' | 'md' | 'lg';

interface SynButtonProps {
    variant?:   SynVariant;
    size?:      SynSize;
    loading?:   boolean;
    disabled?:  boolean;
    fullWidth?: boolean;
    startIcon?: React.ReactNode;
    endIcon?:   React.ReactNode;
    onClick?:   () => void;
    type?:      'button' | 'submit' | 'reset';
    sx?:        SxProps<Theme>;
    children:   React.ReactNode;
}

const variantStyles: Record<SynVariant, object> = {
    primary: {
        background: T.gradient.teal,
        color: '#fff',
        border: 'none',
        boxShadow: T.shadow.button,
        '&:hover:not(:disabled)': { boxShadow: T.shadow.buttonHover, filter: 'brightness(1.06)' },
        '&:active:not(:disabled)': { transform: 'scale(0.98)' },
    },
    accent: {
        background: T.gradient.accent,
        color: '#fff',
        border: 'none',
        boxShadow: T.shadow.coralGlow,
        '&:hover:not(:disabled)': { filter: 'brightness(1.06)', boxShadow: `${T.shadow.coralGlow}, ${T.shadow.button}` },
        '&:active:not(:disabled)': { transform: 'scale(0.98)' },
    },
    ghost: {
        background: 'rgba(10,42,56,0.05)',
        color: T.color.text.primary,
        border: `1px solid ${T.color.border.medium}`,
        boxShadow: 'none',
        '&:hover:not(:disabled)': { background: 'rgba(10,42,56,0.09)', borderColor: T.color.teal[500] },
    },
    outline: {
        background: 'transparent',
        color: T.color.teal[600],
        border: `1.5px solid ${T.color.teal[500]}`,
        boxShadow: 'none',
        '&:hover:not(:disabled)': { background: 'rgba(42,140,160,0.06)' },
    },
    danger: {
        background: T.color.status.redBg,
        color: T.color.status.red,
        border: `1px solid ${T.color.status.redBdr}`,
        boxShadow: 'none',
        '&:hover:not(:disabled)': { background: `rgba(220,38,38,0.16)` },
    },
};

const sizeStyles: Record<SynSize, object> = {
    sm: { fontSize: '0.800rem', px: '14px', py: '7px',  borderRadius: `${T.radius.sm}px` },
    md: { fontSize: '0.875rem', px: '20px', py: '10px', borderRadius: `${T.radius.sm}px` },
    lg: { fontSize: '0.950rem', px: '28px', py: '13px', borderRadius: `${T.radius.md}px` },
};

export function SynButton({
    variant = 'primary',
    size = 'md',
    loading = false,
    disabled = false,
    fullWidth = false,
    startIcon,
    endIcon,
    onClick,
    type = 'button',
    sx,
    children,
}: SynButtonProps) {
    const isDisabled = disabled || loading;

    return (
        <Box
            component="button"
            type={type}
            onClick={!isDisabled ? onClick : undefined}
            disabled={isDisabled}
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                fontFamily: T.font.family,
                fontWeight: 600,
                letterSpacing: '0.01em',
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                width: fullWidth ? '100%' : 'auto',
                transition: 'all 0.16s ease',
                outline: 'none',
                lineHeight: 1.4,
                opacity: isDisabled ? 0.54 : 1,
                position: 'relative',
                userSelect: 'none',
                ...variantStyles[variant],
                ...sizeStyles[size],
                ...(sx as object),
            }}
        >
            {loading && (
                <CircularProgress size={14} sx={{
                    color: 'inherit',
                    position: 'absolute',
                    left: '50%',
                    marginLeft: '-7px',
                }} />
            )}
            <Box sx={{ opacity: loading ? 0 : 1, display: 'contents' }}>
                {startIcon}
                {children}
                {endIcon}
            </Box>
        </Box>
    );
}
